import {
  faChevronDown,
  faPlus,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import { API } from "../utils/Apis";
import { showToast } from "../utils/helpers";
import { DeleteLoadingState } from "../Constants/Interfaces";

interface TagsInputListType {
  field:
    | "vibe"
    | "eventType"
    | "occassion"
    | "architect"
    | "features"
    | "amenities"
    | "other";
  type: "Venue" | "Space";
  label?: string;
  placeHolder?: string;
  items: Array<any> | [];
  updateTags?: Function;
  error?: string;
  venueId?: string;
  spaceId?: string;
  systemItems: Array<string> | [];
}

const TagsInputList: React.FC<TagsInputListType> = ({
  field,
  type = "Venue",
  label,
  items = [],
  placeHolder = "",
  updateTags,
  error = "",
  venueId = "",
  spaceId = "",
  systemItems = [],
}) => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [showList, setShowList] = useState(false);

  const [loading, setLoading] = useState(false);
  const [delLoading, setDelLoading] = useState<DeleteLoadingState>({});
  const [newItem, setNewItem] = useState("");
  const [tagErr, setTagErr] = useState("");
  // suggestions will be systemItems minus already selected items
  const [suggestions, setSuggestions] = useState<Array<string>>(
    (systemItems as Array<string>) || []
  );

  const getSelectedTexts = (itemsArr: Array<any>) =>
    itemsArr
      .map((it) => (typeof it === "string" ? it : it?.text))
      .filter(Boolean);

  // keep suggestions in sync with systemItems and selected items
  useEffect(() => {
    const selected = getSelectedTexts(items as Array<any>);
    const filtered = (systemItems as Array<string>).filter(
      (s) => !selected.includes(s)
    );
    setSuggestions(filtered);
  }, [systemItems, items]);

  const updateLocalTags = (newItemObj: object) => {
    const tempItem = [...(items as Array<any>)];
    tempItem.push(newItemObj);

    if (updateTags) {
      updateTags(tempItem);
    }
  };

  const addNewItem = (tempNewItem = "") => {
    const newItemAdd = tempNewItem ? tempNewItem : newItem;

    if (newItemAdd === "") {
      setTagErr(`Please enter ${label} value`);
    } else if (!systemItems.includes(newItemAdd)) {
      setTagErr(`Please select an option from the list.`);
    } else {
      setTagErr("");

      const finalizeAdd = (tagId?: string) => {
        setNewItem("");
        // push { id?, text, type }
        if (tagId) {
          updateLocalTags({ id: tagId, text: newItemAdd, type: field });
        } else {
          // local fallback
          updateLocalTags({ text: newItemAdd });
        }
        // remove from suggestions locally (optimistic) — real sync happens in useEffect
        setSuggestions((prev) => prev.filter((s) => s !== newItemAdd));
      };

      if (type === "Venue" && venueId) {
        if (!loading) {
          setLoading(true);

          API.createTag({
            type: field,
            text: newItemAdd,
            venueId,
          })
            .then((res) => {
              if (res.type === "success") {
                const tagId = res?.data?.id;
                finalizeAdd(tagId);
              }
              showToast(res.type, res.message);
            })
            .catch((err) => {
              showToast("error", err.message);
            })
            .finally(() => setLoading(false));
        }
      } else if (type === "Space" && venueId && spaceId) {
        if (!loading) {
          setLoading(true);

          const tagData = {
            type: field,
            text: newItemAdd,
            venueId,
            spaceId,
          };

          if (["vibe", "occassion", "eventType"].includes(field)) {
            API.createTag(tagData)
              .then((res) => {
                if (res.type === "success") {
                  const tagId = res?.data?.id;
                  finalizeAdd(tagId);
                }
                showToast(res.type, res.message);
              })
              .catch((err) => {
                showToast("error", err.message);
              })
              .finally(() => setLoading(false));
          } else {
            API.createFeaturesTag(tagData)
              .then((res) => {
                if (res.type === "success") {
                  const tagId = res?.data?.id;
                  finalizeAdd(tagId);
                }
                showToast(res.type, res.message);
              })
              .catch((err) => {
                showToast("error", err.message);
              })
              .finally(() => setLoading(false));
          }
        }
      } else {
        // local-only flow
        const tempItemArr = [...(items as Array<any>)];
        tempItemArr.push({ text: newItemAdd });
        setNewItem("");

        if (updateTags) {
          updateTags(tempItemArr);
        }

        // remove from suggestions locally
        setSuggestions((prev) => prev.filter((s) => s !== newItemAdd));
      }
    }
  };

  const deleteItem = (
    item: { id?: string; text?: string },
    itemIndex: number
  ) => {
    // prevent double delete
    if (!item?.id || !delLoading[item.id]) {
      setDelLoading((prev) => ({ ...prev, [item?.id || itemIndex]: true }));

      if (item && item.id) {
        API.deleteTag({ id: item.id })
          .then((res) => {
            showToast(res.type, res.message);

            if (res.type === "success") {
              const tempItems = [...(items as Array<any>)];
              tempItems.splice(itemIndex, 1);
              if (updateTags) {
                updateTags(tempItems);
              }
              // if the removed item's text exists in systemItems, add it back to suggestions
              if (
                item.text &&
                (systemItems as Array<string>).includes(item.text)
              ) {
                setSuggestions((prev) => {
                  if (!prev.includes(item.text!)) {
                    return [...prev, item.text!];
                  }
                  return prev;
                });
              }
            }
          })
          .catch((err) => {
            showToast("error", err.message);
          })
          .finally(() =>
            setDelLoading((prev) => ({
              ...prev,
              [item?.id || itemIndex]: false,
            }))
          );
      } else {
        // local remove (no id)
        const tempItems = [...(items as Array<any>)];
        const removed = tempItems.splice(itemIndex, 1);
        if (updateTags) {
          updateTags(tempItems);
        }
        // restore to suggestions if part of systemItems
        const removedText = removed?.[0]?.text;
        if (
          removedText &&
          (systemItems as Array<string>).includes(removedText)
        ) {
          setSuggestions((prev) => {
            if (!prev.includes(removedText)) {
              return [...prev, removedText];
            }
            return prev;
          });
        }
        setDelLoading((prev) => ({ ...prev, [item?.id || itemIndex]: false }));
      }
    }
  };

  useEffect(() => {
    setTagErr(error);
  }, [error]);

  return (
    <div className="form-group">
      <div className="d-flex justify-content-between align-items-center">
        <label>{label}</label>
      </div>

      <div className="position-relative tag-dropdown-input">
        <input
          ref={searchInputRef}
          type="text"
          className="form-control"
          placeholder={placeHolder}
          name="tag_name"
          value={newItem}
          autoComplete="off"
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addNewItem();
            }
          }}
          onFocus={() => {
            setShowList(true);
          }}
          onBlur={() => setTimeout(() => setShowList(false), 100)}
        />

        <span
          className={`tag-dropdown-caret as-link${
            showList ? " is-open" : ""
          }`}
          onMouseDown={(e) => {
            // prevent input blur so toggle is reliable
            e.preventDefault();
            if (showList) {
              setShowList(false);
              if (searchInputRef.current) searchInputRef.current.blur();
            } else {
              setShowList(true);
              if (searchInputRef.current) searchInputRef.current.focus();
            }
          }}
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </span>

        {showList && suggestions.length > 0 && (
          <ul className="suggestion-list">
            {suggestions
              .filter((s) =>
                s.toLowerCase().includes(newItem.trim().toLowerCase())
              )
              .map((item) => (
                <li
                  key={item}
                  style={{ padding: "8px", cursor: "pointer" }}
                  onMouseDown={() => {
                    // select item from suggestions
                    setNewItem(item);
                    addNewItem(item);
                    setShowList(false);
                    // blur safely
                    if (searchInputRef.current) searchInputRef.current.blur();
                  }}
                >
                  {item}
                </li>
              ))}
          </ul>
        )}

        {loading && (
          <span className="create-tag-loader">
            <FontAwesomeIcon icon={faSpinner} spin />
          </span>
        )}
      </div>

      {tagErr && <span className="error">{tagErr}</span>}

      {items && (items as Array<any>)?.length > 0 && (
        <div className="selected-item-list">
          <ul>
            {(items as Array<any>).map((item: any, index: number) => (
              <li key={`input-tag-${index}-${item?.text || index}`}>
                {item?.text || item}
                <span
                  className="remove-icon as-link"
                  onClick={() => deleteItem(item, index)}
                >
                  {item?.id && delLoading[item?.id] && (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  )}
                  {(!item?.id || !delLoading[item?.id]) && (
                    <FontAwesomeIcon icon={faTimes} />
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TagsInputList;
